import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { trackPageView } from "../Services/analytics";

const pageMeta = {
  "/": {
    title: "LEAN ASIA — Mobile App Development Studio",
    description: "We build lean, high-performance, user-friendly mobile apps. Specializing in iOS, Android, UI/UX design and ongoing support.",
  },
  "/about": {
    title: "About Us — LEAN ASIA",
    description: "Learn about LEAN ASIA, our mission, values, and the team behind high-performance mobile apps.",
  },
  "/services": {
    title: "Services — LEAN ASIA",
    description: "Mobile app development, UI/UX design, and ongoing support & maintenance services by LEAN ASIA.",
  },
  "/case-studies": {
    title: "Case Studies — LEAN ASIA",
    description: "See how LEAN ASIA has helped businesses achieve their goals with mobile app solutions.",
  },
  "/team": {
    title: "Our Team — LEAN ASIA",
    description: "Meet the talented people behind LEAN ASIA's mobile app development studio.",
  },
  "/faq": {
    title: "FAQ — LEAN ASIA",
    description: "Frequently asked questions about LEAN ASIA's mobile app development services, pricing, and process.",
  },
  "/blog": {
    title: "Blog & Insights — LEAN ASIA",
    description: "Thoughts on mobile development, UI/UX design, and technology trends from LEAN ASIA.",
  },
  "/contact": {
    title: "Contact Us — LEAN ASIA",
    description: "Get in touch with LEAN ASIA to discuss your next mobile app project.",
  },
  "/support": {
    title: "Support Center — LEAN ASIA",
    description: "Need help? Contact LEAN ASIA support team for assistance with our products and services.",
  },
  "/privacy-policy": {
    title: "Privacy Policy — LEAN ASIA",
    description: "LEAN ASIA privacy policy. Learn how we collect, use, and protect your data.",
  },
  "/terms-of-use": {
    title: "Terms of Use — LEAN ASIA",
    description: "LEAN ASIA terms of use and service agreement.",
  },
  "/app/gpsmapcamera": {
    title: "GPS Map Camera — LEAN ASIA",
    description: "GPS Map Camera automatically adds GPS location, time, date, map, and weather info to your photos.",
  },
  "/app/birthdayreminder": {
    title: "Birthday Alarm & Calendar — LEAN ASIA",
    description: "Never miss a birthday again. Smart reminders, calendar sync, and custom messages.",
  },
  "/app/aicloset": {
    title: "AI Clothes Changer — LEAN ASIA",
    description: "Virtual try-on powered by AI. See how clothes look on you instantly.",
  },
  "/app/ledscroller": {
    title: "LED Scroller — LEAN ASIA",
    description: "Transform your device into a digital LED sign with scrolling text and effects.",
  },
  "/tech-stack": {
    title: "Technology Stack — LEAN ASIA",
    description: "Explore the technologies and tools LEAN ASIA uses to build high-performance mobile apps.",
  },
  "/changelog": {
    title: "Changelog — LEAN ASIA",
    description: "What's new at LEAN ASIA. Latest updates, milestones, and launches.",
  },
  "/status": {
    title: "System Status — LEAN ASIA",
    description: "Check the operational status of LEAN ASIA services and systems.",
  },
  "/sitemap": {
    title: "Sitemap — LEAN ASIA",
    description: "Navigate all pages on the LEAN ASIA website.",
  },
  "/comparison": {
    title: "LEAN ASIA vs Others — Compare Services",
    description: "Compare LEAN ASIA with typical agencies and freelancers. See why we're the right choice.",
  },
  "/industries": {
    title: "Industries We Serve — LEAN ASIA",
    description: "LEAN ASIA builds mobile apps across photography, lifestyle, fashion, communication, and more.",
  },
  "/cost-calculator": {
    title: "Project Cost Estimator — LEAN ASIA",
    description: "Estimate the cost of your mobile app project with our interactive calculator.",
  },
  "/roi-calculator": {
    title: "App ROI Calculator — LEAN ASIA",
    description: "Calculate the return on investment for your mobile app project.",
  },
  "/quiz": {
    title: "What App Do You Need? — LEAN ASIA",
    description: "Take our quick quiz to find out which mobile app solution is right for your business.",
  },
  "/request-proposal": {
    title: "Request a Proposal — LEAN ASIA",
    description: "Submit a detailed proposal request for your next mobile app project.",
  },
  "/glossary": {
    title: "Glossary — LEAN ASIA",
    description: "Key terms and definitions in mobile app development explained simply.",
  },
  "/app-stats": {
    title: "App Performance Dashboard — LEAN ASIA",
    description: "See real-time performance stats for all LEAN ASIA mobile apps.",
  },
  "/webinar": {
    title: "Free Workshop — LEAN ASIA",
    description: "Register for our free workshop on mobile app development.",
  },
  "/coming-soon": {
    title: "Coming Soon — LEAN ASIA",
    description: "Something big is coming. Be the first to know about our next launch.",
  },
};

const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    const meta = pageMeta[location.pathname] || {
      title: "LEAN ASIA — Mobile App Development Studio",
      description: "We build lean, high-performance, user-friendly mobile apps.",
    };

    document.title = meta.title;

    const descTag = document.querySelector('meta[name="description"]');
    if (descTag) descTag.setAttribute("content", meta.description);

    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute("content", meta.title);

    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute("content", meta.description);

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute("content", `https://leanasia.com${location.pathname}`);

    const twTitle = document.querySelector('meta[name="twitter:title"]');
    if (twTitle) twTitle.setAttribute("content", meta.title);

    const twDesc = document.querySelector('meta[name="twitter:description"]');
    if (twDesc) twDesc.setAttribute("content", meta.description);

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", `https://leanasia.com${location.pathname}`);

    trackPageView(meta.title);
  }, [location.pathname]);
};

export default usePageTracking;
